import React, { useState, useEffect } from "react";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { ACTIVITY_STATUS, STATUS_MAPPING } from "../../common/Constants";
import FormRenderer, { ComponentMapper, FormTemplate, componentTypes } from "../../common/FormRenderer";
import { preventDefault } from "../../../utils/common";
import { validatorTypes } from "@data-driven-forms/react-form-renderer";
import { toast } from "react-toastify";

function PublishModal({ onClose, onSubmit, selectedRows, auditType, audit, auditPhase }: any) {
  const [counts, setCounts] = useState<any[]>([]);
  const [form, setForm] = useState<any>({});
  const [details, setDetails] = useState<any>({ hideButtons: true })
  const [treatAsFinalAudit, setTreatAsFinalAudit] = useState<boolean>(false);

  // Determine modal title and messages based on audit phase
  const isPreliminaryAudit = auditPhase === 'preliminary';
  const isFinalAudit = auditPhase === 'final';
  const modalTitle = isPreliminaryAudit ? 'Publish Preliminary Audit' :
                     isFinalAudit ? 'Publish Final Audit' : 'Publish';
  const publishMessage = isPreliminaryAudit ?
    'Upon publishing, the preliminary audit will be published and the audit will move to the final audit phase.' :
    isFinalAudit ?
    'Upon publishing, the final audit will be published. This will complete the audit process.' :
    'Upon publishing, you will not be able to edit them further.';
  const confirmMessage = isPreliminaryAudit ?
    'Do you want to finalize the preliminary audit? Click "Yes"' :
    isFinalAudit ?
    'Do you want to publish the final audit? Click "Yes"' :
    'Do you want to submit ? click "Yes"';

  const schema = auditType === "todo" ? {
    fields: [
      {
        component: componentTypes.TEXT_EDITOR,
        name: 'observations',
        label: 'Observations',
        placeholder: 'Enter Observations...',
        initialValue: audit.observations || ""
      },
      {
        component: componentTypes.TEXT_EDITOR,
        name: 'recommendations',
        label: 'Recommendations',
        placeholder: 'Enter Recommendations...',
        initialValue: audit.recommendations || ""
      }
    ]
  } : {
    fields: [
      {
        component: componentTypes.TEXT_FIELD,
        name: 'vendor_nature_of_work',
        label: 'Vendor Nature of Work',
        placeholder: 'Enter Vendor Nature of Work...',
        validate: [
          { type: validatorTypes.REQUIRED }
        ]
      },
      {
        component: componentTypes.TEXT_FIELD,
        name: 'employee_head_count',
        label: 'Employee Head Count',
        placeholder: 'Enter Employee Head Count...',
        validate: [
          { type: validatorTypes.REQUIRED }
        ]
      },
      {
        component: componentTypes.DATE_PICKER,
        name: 'audit_start_date',
        label: 'Audit Start Date',
        className: 'grid-col-100',
        range: false,
        clearable: true,
        onChange: onStartDateChange,
        validate: [
          { type: validatorTypes.REQUIRED }
        ]
      },
      {
        component: componentTypes.DATE_PICKER,
        name: 'audit_end_date',
        label: 'Audit End Date',
        className: 'grid-col-100',
        range: false,
        clearable: true,
        onChange: onEndDateChange,
        validate: [
          { type: validatorTypes.REQUIRED }
        ]
      },
      {
        component: componentTypes.TEXT_EDITOR,
        name: 'observations',
        label: 'Observations',
        placeholder: 'Enter Observations...',
        initialValue: audit.observations || ""
      },
      {
        component: componentTypes.TEXT_EDITOR,
        name: 'recommendations',
        label: 'Recommendations',
        placeholder: 'Enter Recommendations...',
        initialValue: audit.recommendations || ""
      }
    ]
  };

  function debugForm(_form: any) {
    setForm(_form);
    setDetails(_form.values);
  }

  function handleSubmit(e: any) {
    preventDefault(e);
    if(auditType == "todo"){
      if (form.valid) {
        const { recommendations, observations } = details;
        onSubmit(e, recommendations, observations, treatAsFinalAudit);
      } else {
        toast.error("Please fill all the mandatory fields.");
      }
    } else {
      if (form.valid) {
        const { recommendations, observations, vendor_nature_of_work, employee_head_count, audit_start_date, audit_end_date } = details;
        onSubmit(e, { recommendations, observations, vendor_nature_of_work, employee_head_count, audit_start_date, audit_end_date }, true, false, treatAsFinalAudit);
      } else {
        toast.error("Please fill all the mandatory fields.");
      }
    }
  }

  useEffect(() => {
    if (selectedRows) {
      const _counts: any = {};
      selectedRows.forEach((row: any) => {
        if (!_counts[row.status]) {
          _counts[row.status] = 0;
        }
        _counts[row.status] = _counts[row.status] + 1;
      });

      setCounts(Object.keys(_counts).map((key: any) => {
        return {
          key,
          label: key === ACTIVITY_STATUS.PENDING ? `${STATUS_MAPPING[key]} (No Audit Activities)` :  STATUS_MAPPING[key],
          count: _counts[key]
        }
      }));
    }
  }, [selectedRows]);

  function onStartDateChange(event: any) {
    setDetails({ ...details, audit_start_date: event })
  }

  function onEndDateChange(event: any) {
    setDetails({ ...details, audit_end_date: event })
  }

  return (
    <>
      <Modal show={true} backdrop="static" animation={false} size="lg">
        <Modal.Header closeButton={true} onHide={onClose}>
          <Modal.Title className="bg">{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row mb-4">
            <div className="col-12">
              {/* <p>There are <strong>{selectedRows.length} {selectedRows.length > 1 ? 'activities' : 'activity'}</strong> available for publishing.</p>
              <div className="row justify-content-center my-3">
                <div className="col-8">
                  {
                    counts.map(count => {
                      return (
                        <div className="row my-1" key={count.key}>
                          <div className={`col-7 status-${count.key}`}><strong>{count.label}</strong></div>
                          <div className="col-4">{count.count}</div>
                        </div>
                      )
                    })
                  }
                </div>
              </div> */}
              <div className="col-12">
                <div className="d-flex flex-column align-items-center mb-4">
                  <div className="col-6">
                    <div className="custom-card">
                        <div className="stat">
                            <div className="stat-value">{selectedRows.length}</div>
                            <div className="stat-label">Total Audits</div>
                        </div>
                        {
                          counts.map(count => {
                            return (
                              <React.Fragment key={count.key}>
                                <div className="divider"></div>
                                <div className="stat">
                                    <div className="stat-value">{count.count}</div>
                                    <div className="stat-label">{count.label}</div>
                                </div>
                              </React.Fragment>
                            )
                          })
                        }
                    </div>
                  </div>
                </div>
              </div>
              <p>{publishMessage}</p>
              <p>{confirmMessage}</p>
              <p>To cancel, click "No"</p>

              {isPreliminaryAudit && (
                <div className="mb-3">
                  <Form.Check
                    type="switch"
                    id="treat-as-final-audit"
                    label="Consider this as final audit"
                    checked={treatAsFinalAudit}
                    onChange={(e) => setTreatAsFinalAudit(e.target.checked)}
                    reverse={true}
                    inline={true}
                    className="mb-2"
                    style={{fontWeight: '600'}}
                  />
                  {treatAsFinalAudit && (
                    <small className="text-muted d-block">
                      This will publish the final audit and complete the audit process.
                    </small>
                  )}
                  {!treatAsFinalAudit && (
                    <small className="text-muted d-block">
                      This will publish only the preliminary audit.
                    </small>
                  )}
                </div>
              )}

              <FormRenderer FormTemplate={FormTemplate}
                initialValues={details}
                componentMapper={ComponentMapper}
                schema={schema}
                debug={debugForm}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={onClose} className="btn btn-outline-secondary">
            No
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default PublishModal;